import {
  STUDIO_WORKSPACE_GROUP_PROPERTY,
  STUDIO_WORKSPACE_GROUP_PROPERTY_VALUE,
} from 'src/constants/studioFtux';

/**
 * Environment cohort (LocalStack / Railway preview), not a product cohort.
 * Must match backend `posthogservice.LocalstackWorkspaceGroupTrait` / Value
 * (`"localstack"` / `"true"`). Stamp only — do not change flag rollouts.
 */
export const LOCALSTACK_WORKSPACE_GROUP_PROPERTY = 'localstack';
export const LOCALSTACK_WORKSPACE_GROUP_PROPERTY_VALUE = 'true';

/**
 * Workspace group properties for PostHog client `group()` / SSR `getAllFlags`.
 * Studio portals include the cohort trait so flags targeting `studio="true"`
 * evaluate before (or without relying on) async `$groupidentify` ingest.
 * LocalStack portals include `localstack="true"` the same way — ingest is
 * async, and a new portal ID may never match stored groups.
 *
 * Traits derive from persisted PortalConfig (Studio) and the Cognito proxy
 * endpoint (LocalStack), so every dashboard load re-asserts them: a
 * `$groupidentify` the backend failed to deliver at workspace creation is
 * repaired here rather than leaving the workspace untargetable.
 */
export const getWorkspacePosthogGroupProperties = (
  portalName: string,
  isStudioUser: boolean,
  isLocalstack: boolean,
): Record<string, string> => ({
  name: portalName,
  ...getStudioGroupProperties(isStudioUser),
  ...getLocalstackGroupProperties(isLocalstack),
});

/** Options bag for posthog-node `getAllFlags` workspace evaluation. */
export const getPosthogGetAllFlagsOptions = (
  portalHeader: string,
  isStudioUser: boolean,
  isLocalstack: boolean,
): {
  groups: { workspace: string };
  groupProperties?: {
    workspace: Record<string, string>;
  };
} => {
  const workspaceProperties = {
    ...getStudioGroupProperties(isStudioUser),
    ...getLocalstackGroupProperties(isLocalstack),
  };
  return {
    groups: { workspace: portalHeader },
    ...(Object.keys(workspaceProperties).length > 0
      ? { groupProperties: { workspace: workspaceProperties } }
      : {}),
  };
};

/**
 * Cache key so Studio, Classic, and LocalStack evaluations never share a
 * cached result — they resolve different flags and carry different group
 * properties.
 */
export const getPosthogFlagsCacheKey = (
  portalHeader: string,
  isStudioUser: boolean,
  isLocalstack: boolean,
): string => {
  let key = portalHeader;
  if (isStudioUser) {
    key += ':studio';
  }
  if (isLocalstack) {
    key += ':localstack';
  }
  return key;
};

const getStudioGroupProperties = (
  isStudioUser: boolean,
): Record<string, string> =>
  isStudioUser
    ? {
        [STUDIO_WORKSPACE_GROUP_PROPERTY]:
          STUDIO_WORKSPACE_GROUP_PROPERTY_VALUE,
      }
    : {};

const getLocalstackGroupProperties = (
  isLocalstack: boolean,
): Record<string, string> =>
  isLocalstack
    ? {
        [LOCALSTACK_WORKSPACE_GROUP_PROPERTY]:
          LOCALSTACK_WORKSPACE_GROUP_PROPERTY_VALUE,
      }
    : {};
